import { CheckIcon } from '../../components/ui/index.jsx'

// Where a registration stands (V013, V014).
//
// Registering builds the whole shop at once, but nobody in it can sign in
// until two things have happened: the owner has opened the link we emailed, to
// prove the address is theirs, and platform support has approved the shop.
// The register page shows where that stands — and keeps asking, so it moves on
// by itself while the owner is looking at it — and the sign-in card says the
// same when somebody tries before then. Both take their words from here, so
// the owner reads one description of the wait wherever they meet it.

/**
 * Where a registration is, from what the server says about it:
 *   verify    waiting on the owner — the link is unopened
 *   review    waiting on the platform
 *   approved  waiting on nobody: sign in
 */
export function registrationStage({ approval_status: status, email_verified: verified }) {
  if (status === 'approved') return 'approved'
  return verified ? 'review' : 'verify'
}

const STEPS = [
  {
    key: 'registered',
    title: () => 'Registered',
    note: () => 'Your shop and your owner login are set up.',
  },
  {
    key: 'email',
    title: (stage) => (stage === 'verify' ? 'Confirm your email' : 'Email confirmed'),
    note: (stage, email) =>
      stage === 'verify'
        ? `Open the link we sent to ${email || 'your email'}. It shows the address is yours.`
        : `${email || 'Your address'} is confirmed as yours.`,
  },
  {
    key: 'review',
    title: (stage) => (stage === 'approved' ? 'Approved' : 'Under review'),
    note: (stage) =>
      stage === 'approved'
        ? 'Your shop has been checked and let in.'
        : 'We check every new shop before anyone in it can sign in.',
  },
  {
    key: 'sign-in',
    title: () => 'Sign in',
    note: (stage, email) =>
      stage === 'approved'
        ? `With ${email || 'your email'} and the password you chose.`
        : 'As soon as the shop is approved. This page shows it the moment it is.',
  },
]

// The step in progress at each stage.
const CURRENT = { verify: 1, review: 2, approved: 3 }

/** The four steps, with the one in progress marked. */
export function ApprovalSteps({ stage = 'review', email }) {
  const current = CURRENT[stage] ?? CURRENT.review
  return (
    <ol className="auth-steps" aria-label="Where your registration stands">
      {STEPS.map((step, index) => {
        const state = index < current ? 'is-done' : index === current ? 'is-current' : ''
        return (
          <li
            key={step.key}
            className={`auth-step ${state}`.trim()}
            aria-current={index === current ? 'step' : undefined}
          >
            <span className="auth-step-number" aria-hidden="true">
              {index < current ? <CheckIcon /> : index + 1}
            </span>
            <span className="auth-step-text">
              <span className="auth-step-title">{step.title(stage)}</span>
              <span className="auth-step-note">{step.note(stage, email)}</span>
            </span>
          </li>
        )
      })}
    </ol>
  )
}

const NOTICE_TITLE = {
  verify: 'Verify your email',
  pending: 'Waiting for approval',
  rejected: 'Not approved',
  suspended: 'This shop is switched off',
  // The register page, when the confirmation email could not be sent.
  mail: 'The email did not go',
}

/**
 * What the sign-in card — and the register page, for a rejection — shows in
 * place of a toast when the shop has not been let in.
 *
 * On the card rather than in a toast because it is not a mistake the person
 * made and it does not go away: a toast that fades leaves them looking at a
 * form that apparently did nothing. The sentence is the server's — it names
 * the shop, and carries what only the server knows: the address a new link
 * just went to, or the reviewer's reason.
 */
export function ApprovalNotice({ status, message, children }) {
  return (
    <div
      className={`auth-notice${status === 'rejected' || status === 'suspended' ? ' is-crit' : ''}`}
      role="status"
    >
      <p className="auth-notice-title">{NOTICE_TITLE[status] ?? NOTICE_TITLE.pending}</p>
      <p>{message}</p>
      {children}
    </div>
  )
}
